import { injectable } from 'inversify';
import type {
  IPresentationCreatorRegistrar,
  IThreeDObjectPresentationCreator,
  IThreeDMeasurementPresentationCreator,
  ITwoDObjectPresentationCreator,
  ITwoDMeasurementPresentationCreator,
  SceneItemKind,
  IImageThreeDObjectPresentationCreator,
  IImageThreeDMeasurementPresentationCreator,
  IImageTwoDObjectPresentationCreator,
  IImageTwoDMeasurementPresentationCreator,
  ITwoDMeasurementLabelPresentationCreator,
  IPresentationCreatorImagesRegistry,
  IPresentationCreatorThreeDRegistry
} from '@/engine/app/interfaces.ts';

@injectable()
export class PresentationCreatorsRegistry
  implements IPresentationCreatorRegistrar, IPresentationCreatorImagesRegistry, IPresentationCreatorThreeDRegistry
{
  // 3D presentation registries Maps
  private readonly _threeDObjectsRegistry: Map<string, IThreeDObjectPresentationCreator> = new Map();

  private readonly _threeDMeasurementRegistry: Map<string, IThreeDMeasurementPresentationCreator> = new Map();

  private readonly _twoDObjectRegistry: Map<string, ITwoDObjectPresentationCreator> = new Map();

  private readonly _twoDMeasurementRegistry: Map<string, ITwoDMeasurementPresentationCreator> = new Map();

  private readonly _twoDMeasurementLabelRegistry: Map<string, ITwoDMeasurementLabelPresentationCreator> = new Map();

  // Image presentation registries

  private readonly _imageThreeDObjectsRegistry: Map<string, IImageThreeDObjectPresentationCreator> = new Map();

  private readonly _imageThreeDMeasurementRegistry: Map<string, IImageThreeDMeasurementPresentationCreator> = new Map();

  private readonly _imageTwoDObjectRegistry: Map<string, IImageTwoDObjectPresentationCreator> = new Map();

  private readonly _imageTwoDMeasurementRegistry: Map<string, IImageTwoDMeasurementPresentationCreator> = new Map();

  // 3D presentation registries
  public registerThreeDObjectCreator(type: SceneItemKind, creator: IThreeDObjectPresentationCreator): void {
    this._threeDObjectsRegistry.set(type, creator);
  }

  public registerThreeDMeasurementCreator(type: SceneItemKind, creator: IThreeDMeasurementPresentationCreator): void {
    this._threeDMeasurementRegistry.set(type, creator);
  }

  public registerTwoDObjectCreator(type: SceneItemKind, creator: ITwoDObjectPresentationCreator): void {
    this._twoDObjectRegistry.set(type, creator);
  }

  public registerTwoDMeasurementCreator(type: SceneItemKind, creator: ITwoDMeasurementPresentationCreator): void {
    this._twoDMeasurementRegistry.set(type, creator);
  }

  public registerTwoDMeasurementLabelCreator(
    type: SceneItemKind,
    creator: ITwoDMeasurementLabelPresentationCreator
  ): void {
    this._twoDMeasurementLabelRegistry.set(type, creator);
  }

  // Image presentation registries
  public registerImageThreeDObjectCreator(type: SceneItemKind, creator: IImageThreeDObjectPresentationCreator): void {
    this._imageThreeDObjectsRegistry.set(type, creator);
  }

  public registerImageThreeDMeasurementCreator(
    type: SceneItemKind,
    creator: IImageThreeDMeasurementPresentationCreator
  ): void {
    this._imageThreeDMeasurementRegistry.set(type, creator);
  }

  public registerImageTwoDObjectCreator(type: SceneItemKind, creator: IImageTwoDObjectPresentationCreator): void {
    this._imageTwoDObjectRegistry.set(type, creator);
  }

  public registerImageTwoDMeasurementCreator(
    type: SceneItemKind,
    creator: IImageTwoDMeasurementPresentationCreator
  ): void {
    this._imageTwoDMeasurementRegistry.set(type, creator);
  }

  // 3D presentation resolvers
  public resolveThreeDObjectCreator(type: SceneItemKind): IThreeDObjectPresentationCreator | undefined {
    if (!this._threeDObjectsRegistry.has(type)) return;
    return this._threeDObjectsRegistry.get(type);
  }

  public resolveThreeDMeasurementCreator(type: SceneItemKind): IThreeDMeasurementPresentationCreator | undefined {
    if (!this._threeDMeasurementRegistry.has(type)) return;
    return this._threeDMeasurementRegistry.get(type);
  }

  public resolveTwoDObjectCreator(type: SceneItemKind): ITwoDObjectPresentationCreator | undefined {
    if (!this._twoDObjectRegistry.has(type)) return;
    return this._twoDObjectRegistry.get(type);
  }

  public resolveTwoDMeasurementCreator(type: SceneItemKind): ITwoDMeasurementPresentationCreator | undefined {
    if (!this._twoDMeasurementRegistry.has(type)) return;
    return this._twoDMeasurementRegistry.get(type);
  }

  public resolveTwoDMeasurementLabelCreator(type: SceneItemKind): ITwoDMeasurementLabelPresentationCreator | undefined {
    if (!this._twoDMeasurementLabelRegistry.has(type)) return;
    return this._twoDMeasurementLabelRegistry.get(type);
  }

  // Image presentation resolvers

  public resolveImageThreeDObjectCreator(type: SceneItemKind): IImageThreeDObjectPresentationCreator | undefined {
    if (!this._imageThreeDObjectsRegistry.has(type)) return;
    return this._imageThreeDObjectsRegistry.get(type);
  }

  public resolveImageThreeDMeasurementCreator(
    type: SceneItemKind
  ): IImageThreeDMeasurementPresentationCreator | undefined {
    if (!this._imageThreeDMeasurementRegistry.has(type)) return;
    return this._imageThreeDMeasurementRegistry.get(type);
  }

  public resolveImageTwoDObjectCreator(type: SceneItemKind): IImageTwoDObjectPresentationCreator | undefined {
    if (!this._imageTwoDObjectRegistry.has(type)) return;
    return this._imageTwoDObjectRegistry.get(type);
  }

  public resolveImageTwoDMeasurementCreator(type: SceneItemKind): IImageTwoDMeasurementPresentationCreator | undefined {
    if (!this._imageTwoDMeasurementRegistry.has(type)) return;
    return this._imageTwoDMeasurementRegistry.get(type);
  }
}
